import { inject, Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  CollectionReference,
  doc,
  docSnapshots,
  DocumentReference,
  Firestore,
  getDoc,
  query,
  QueryConstraint,
  setDoc,
  updateDoc
} from '@angular/fire/firestore';
import { catchError, map, Observable, throwError } from 'rxjs';

/**
 * Tag a Firestore listener error in place with its collection/doc path — the
 * minified webchannel stack traces in New Relic name only SDK internals. Mutates
 * the same instance so the tag survives both a consumer's subscribe(error) and a
 * leak through Zone -> GlobalErrorHandler.
 */
function tagFirestoreError<T>(op: string, path: string) {
  return (source: Observable<T>): Observable<T> =>
    source.pipe(
      catchError((err) => {
        if (err && typeof err === 'object') {
          const e = err as { firestoreOp?: string; firestorePath?: string; __pathTagged?: boolean; message?: string };
          e.firestoreOp = op;
          e.firestorePath = path;
          if (!e.__pathTagged) {
            e.__pathTagged = true;
            // Prefix message so even a stack-only NR report is identifiable.
            try {
              e.message = `[${op} ${path}] ${e.message ?? ''}`;
            } catch {
              /* readonly message — tags above suffice */
            }
          }
        }
        return throwError(() => err);
      })
    );
}

@Injectable({
  providedIn: 'root'
})
export class DbService {
  private _db = inject(Firestore);

  constructor() {}

  createId<T>(collectionName: string): { id: string; ref: DocumentReference<T> } {
    const newDocRef = doc(collection(this._db, collectionName));
    return { id: newDocRef.id, ref: newDocRef } as { id: string; ref: DocumentReference<T> };
  }

  readDoc<T>(collectionName: string, docId: string): Promise<T> {
    const ref = doc(this._db, collectionName, docId) as DocumentReference<T>;
    return getDoc(ref).then((snap) => {
      return snap.data() as T;
    });
  }

  queryDoc$<T>(collectionName: string, docId: string): Observable<T> {
    const ref = doc(this._db, collectionName, docId) as DocumentReference<T>;
    return docSnapshots<T>(ref).pipe(
      map((s) => {
        return s.data();
      }),
      tagFirestoreError(`docSnapshots`, `${collectionName}/${docId}`)
    );
  }

  queryCollection$<T>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Observable<T[]> {
    const col = collection(this._db, collectionName) as CollectionReference<T>;
    const q = query(col, ...constraints);
    return collectionData<T>(q).pipe(tagFirestoreError(`collectionData`, collectionName));
  }

  async setDoc(collectionName: string, docId: string, data: any): Promise<void> {
    await setDoc(doc(this._db, collectionName, docId), data);
    return;
  }

  updateDoc(collectionName: string, docId: string, data: any) {
    return updateDoc(doc(this._db, collectionName, docId), data);
  }
}
